import React, {
  ChangeEventHandler,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import { Grid, Icons, Typography } from '@nextiva/next-ui-components';
import { FeatureContext } from 'components/AccessResolvers/featureContext';
import { uploadBulkImport } from 'services';
import {
  SvgUserIcon,
  SvgLogo,
  SvgImport,
  SvgLocationIcon,
  SvgPhoneIcon,
  SvgTeamIcon,
} from 'components/SvgIcon';
import { LoadingIcon } from './HistoryLog.styles';
import {
  BoldText,
  StyledContainer,
  HiddenInput,
  LabelText,
  LongTextButton,
  FileTypeAlert,
  GrayIcon,
  StyledBody,
  BottomButton,
  StyledTypographyMessage,
  StyledImportTypography,
  StyledCircularProgress,
  StyledRemoveFileButton,
  StyledFooterGrid,
  StyledFooterButton,
  StyledSelectedFileGrid,
  StyledNameGrid,
} from './DataImport.styles';
import { DataImportContext } from './context';
import { DownloadTemplate } from './DownloadTemplate';
import { getTitleFromHeader } from './utils';
import { screens } from '.';

const onDragOver = (event) => {
  event.preventDefault();
  event.stopPropagation();
};

interface Props {
  importHandler: () => void;
  importType: string;
}

const invalidations = {
  USER: ['getUserList', 'licenseList', 'getRoles', 'locations'],
  USER_UPDATE: ['getUserList', 'licenseList', 'getRoles', 'locations'],
  LOCATION: ['locations'],
  TEAM: ['getTeams', 'getUserList'],
};

const getIcon = (importType: string) => {
  switch (importType) {
    case screens.USERS_IMPORT:
    case screens.USER_UPDATE:
      return <SvgUserIcon />;
    case screens.TEAMS_IMPORT:
      return <SvgTeamIcon />;
    case screens.LOCATIONS_IMPORT:
      return <SvgLocationIcon />;
    case screens.PHONES_AND_DEVICES_IMPORT:
      return <SvgPhoneIcon />;
    default:
      return Icons.user;
  }
};

const getText = (importType: string) => {
  switch (importType) {
    case screens.USERS_IMPORT:
    case screens.USER_UPDATE:
      return 'users';
    case screens.TEAMS_IMPORT:
      return 'teams';
    case screens.LOCATIONS_IMPORT:
      return 'locations';
    case screens.PHONES_AND_DEVICES_IMPORT:
      return 'phones & devices';

    default:
      return '';
  }
};

const getDownloadTitle = (importType: string) => {
  switch (importType) {
    case screens.USERS_IMPORT:
      return 'Users';
    case screens.TEAMS_IMPORT:
      return 'Teams';
    case screens.LOCATIONS_IMPORT:
      return 'Locations';
    case screens.PHONES_AND_DEVICES_IMPORT:
      return 'Phones & Devices';
    case screens.USER_UPDATE:
      return 'Edit user';
    default:
      return '';
  }
};

const getMessage = (importType: string) => {
  switch (importType) {
    case screens.USERS_IMPORT:
      return 'To import people that can sign in as users, please download and utilize our specified template.';
    case screens.TEAMS_IMPORT:
      return 'To import team names, phone numbers, and members, please utilize our specified template.';
    case screens.LOCATIONS_IMPORT:
      return 'To import business locations where people work from, please utilize our specified template.';
    case screens.PHONES_AND_DEVICES_IMPORT:
      return 'To import phones & devices that your company uses, please utilize our specified template.';
    case screens.USER_UPDATE:
      return 'To edit people that can sign in as users, please download and utilize our specified template.';
    default:
      return '';
  }
};

const getClickHandler = (importType: string, history) => {
  switch (importType) {
    case screens.USERS_IMPORT:
    case screens.USER_UPDATE:
      return () => history.push('/users');
    case screens.TEAMS_IMPORT:
      return () => history.push('/teams');
    case screens.LOCATIONS_IMPORT:
      return () => {
        localStorage.setItem('accountInfoTabIndex', '1');
        history.push('/accountinfo');
      };
    case screens.PHONES_AND_DEVICES_IMPORT:
      return () =>
        (window.location.href = `${window.location.origin}/apps/voice-admin/#/voice/manage-phones`);
    default:
      return () => null;
  }
};

const ImportData = ({ importHandler, importType }: Props) => {
  const uploadUsersRef = useRef(null);
  const errorFile = useRef(null);
  const history = useHistory();
  const [file, setFile] = useState<File>();
  const [errorMessage, setErrorMessage] = useState(null);
  const [importStatus, setImportStatus] = useState(false);
  const { corpAcctNbr } = useContext(FeatureContext);
  const { handleTabChange, refetchJobLocks } = useContext(DataImportContext);
  const { mutateAsync: uploadBulkImportMutate, isLoading } = useMutation(
    uploadBulkImport,
  );
  const queryClient = useQueryClient();

  useEffect(() => {
    if (file?.name.match(/xls|xlsx/gi) === null) {
      setErrorMessage(
        'File format or extension invalid. Please check that the file extension matches the format of the file using XLS or XLSX.',
      );
    }
  }, [file]);

  const startUpload: ChangeEventHandler<HTMLInputElement> = (event) => {
    setFile(event.target.files[0]);
    setImportStatus(true);
  };
  const uploadFile = () => {
    uploadUsersRef.current?.click();
  };
  const downloadFile = () => {
    const { data: octetStreamData, headers } = errorFile.current;
    const name = getTitleFromHeader(headers['content-disposition']);
    const xlsxDownloadLink = document.createElement('a');
    xlsxDownloadLink.href = URL.createObjectURL(octetStreamData);
    xlsxDownloadLink.download = name;
    xlsxDownloadLink.click();
  };

  const uploadData = async () => {
    const form = new FormData();
    form.append('file', file);
    form.append('entity', importType);

    await uploadBulkImportMutate({
      file: form,
      corpAcctNbr,
      bulkEdit: importType === 'USER_UPDATE',
    })
      .then((response) => {
        if (response.data.type === 'application/octet-stream') {
          errorFile.current = response;
          setErrorMessage(
            <span>
              <b>Template Error</b>: Error in data. Please{' '}
              <u
                onClick={downloadFile}
                onKeyPress={downloadFile}
                style={{ cursor: 'pointer' }}
                role="presentation"
                data-testid="PlatformAdminUI-ImportData-DownloadErrorFile"
              >
                download error file
              </u>
              .
            </span>,
          );
        } else {
          refetchJobLocks();
          handleTabChange(null, 1);
          const queriesToInvalidate = invalidations[importType] || [];
          queriesToInvalidate.forEach((query) => {
            void queryClient.invalidateQueries(query);
          });
        }
      })
      .catch((err) => {
        const { response } = err;
        if (response.headers) {
          if (response.headers['nextiva-context-error']) {
            setErrorMessage(response.headers['nextiva-context-error']);
          } else {
            setErrorMessage(
              response.data.error?.message ?? response.data.error.code,
            );
          }
        }
      });
  };

  const onDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setFile(event.dataTransfer?.files[0]);
    setImportStatus(true);
  };

  const removeFile = () => {
    setFile(null);
    setErrorMessage('');
  };

  useEffect(() => {
    if (file) {
      setImportStatus(false);
    }
  }, [file]);

  return (
    <Grid container sx={{ height: '65vh' }} justifyContent="space-between">
      <Grid
        container
        item
        xs={12}
        flexDirection="row"
        justifyContent="center"
        alignItems="center"
      >
        {getIcon(importType)}
        <GrayIcon
          style={{
            marginLeft: '39px',
            marginRight: '39px',
          }}
          icon={Icons.front}
          size={24}
        />
        <SvgLogo />
      </Grid>
      <Grid
        container
        item
        xs={12}
        sx={{ height: '55vh' }}
        flexDirection="column"
        alignItems="center"
        wrap="nowrap"
      >
        <StyledBody variant="h5">
          {importType === 'USER_UPDATE'
            ? 'Edit existing users'
            : `Import new ${getText(importType)} to Nextiva`}
        </StyledBody>
        <StyledTypographyMessage variant="body1">
          {getMessage(importType)}
        </StyledTypographyMessage>
        <BoldText>It is required to use our templates to import.</BoldText>
        {errorMessage && (
          <FileTypeAlert severity="error" sx={{ marginBottom: '16px' }}>
            {errorMessage}
          </FileTypeAlert>
        )}
        <DownloadTemplate title={getDownloadTitle(importType)} />
        <StyledContainer
          component="div"
          fixed
          maxWidth="md"
          onDrop={onDrop}
          onDragOver={onDragOver}
          data-testid="PlatformAdminUI-ImportData-DropFile"
          style={{ borderStyle: file && 'solid' }}
        >
          {importStatus && <StyledCircularProgress size={40} />}
          {file ? (
            <StyledSelectedFileGrid container flexDirection="column">
              <Grid
                container
                flexDirection="row"
                alignItems="center"
                justifyContent="center"
              >
                <Grid item xs={11}>
                  <LabelText variant="body2">Selected file</LabelText>
                </Grid>
                <Grid item xs={1}>
                  <StyledRemoveFileButton
                    color="primary"
                    variant="text"
                    onClick={removeFile}
                    data-testid="PlatformAdminUI-ImportData-RemoveButton"
                  >
                    Remove
                  </StyledRemoveFileButton>
                </Grid>
              </Grid>
              <Grid container flexDirection="row" alignItems="flex-start">
                <Grid item xs={4}>
                  <LabelText variant="body2">Name</LabelText>
                </Grid>
                <StyledNameGrid item xs={8}>
                  <Typography variant="body2">{file.name}</Typography>
                </StyledNameGrid>
              </Grid>
              <Grid container flexDirection="row" alignItems="center">
                <Grid item xs={4}>
                  <LabelText variant="body2">Size</LabelText>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body2">{file.size / 1000} kb</Typography>
                </Grid>
              </Grid>
              <Grid container flexDirection="row" alignItems="center">
                <Grid item xs={4}>
                  <LabelText variant="body2">Last modified</LabelText>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body2">
                    {new Date(file.lastModified).toDateString()}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container flexDirection="row" alignItems="center">
                <Grid item xs={4}>
                  <LabelText variant="body2">Created</LabelText>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body2">
                    {new Date().toDateString()}
                  </Typography>
                </Grid>
              </Grid>
            </StyledSelectedFileGrid>
          ) : (
            <Grid container flexDirection="row">
              <Grid
                item
                xs={10}
                sx={{
                  ml: '30px',
                  display: 'flex',
                  gap: '25px',
                  justifyContent: 'center',
                }}
              >
                <SvgImport />
                <StyledImportTypography variant="body1">
                  Drag and drop your file here or{' '}
                  <LongTextButton
                    color="primary"
                    size="medium"
                    variant="text"
                    onClick={uploadFile}
                    data-testid="PlatformAdminUI-ImportData-ChooseFile"
                  >
                    choose a file
                  </LongTextButton>{' '}
                  to upload.
                </StyledImportTypography>

                <HiddenInput
                  type="file"
                  ref={uploadUsersRef}
                  accept=".xls,.xlsx"
                  onChange={startUpload}
                  placeholder="uploadCSV"
                  data-testid="PlatformAdminUI-ImportData-UploadFile"
                />
              </Grid>
            </Grid>
          )}
        </StyledContainer>
      </Grid>
      <StyledFooterGrid container item xs={12} justifyContent="space-between">
        <Grid item>
          <StyledFooterButton
            color="secondary"
            size="medium"
            variant="contained"
            sx={{
              height: '40px',
              mr: '16px',
              lineHeight: '1rem',
            }}
            onClick={importHandler}
            data-testid="PlatformAdminUI-ImportData-BackButton"
          >
            Go to data import
          </StyledFooterButton>
          {history.location?.state && (
            <StyledFooterButton
              color="primary"
              size="medium"
              variant="text"
              onClick={getClickHandler(importType, history)}
              data-testid="PlatformAdminUI-ImportData-ReturnButton"
            >
              Back to {getText(importType)}
            </StyledFooterButton>
          )}
        </Grid>
        <BottomButton
          color="primary"
          size="medium"
          variant="contained"
          sx={{
            height: '40px',
            padding: '12px 24px !important',
            lineHeight: '1rem',
          }}
          onClick={uploadData}
          data-testid="PlatformAdminUI-DataImport-StartImport"
          disabled={!file || !!errorMessage || isLoading}
        >
          {isLoading && <LoadingIcon sx={{ color: 'white' }} />}
          Start Import
        </BottomButton>
      </StyledFooterGrid>
    </Grid>
  );
};

export default ImportData;
